import { useCallback, useState } from 'react';
import config from '../config';
import {
  Error as ApiError,
  IdentityCallbackMode,
  IdentityGroup,
  IdentityGroupOrderField,
  IdentityGroupProvider,
  IdentityGroupProviderCheckResult,
  IdentityOAuthTestResult,
  IdentityGroupWebhookTestResult,
  PaginatedRequest,
  PaginatedResponse,
} from '../types';
import pluckParams from '../utilities/pluck-params';
import prepareHeaders from '../utilities/prepare-headers';
import { useAuth } from './auth';

const useIdentityGroups = () => {
  const { token } = useAuth();

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const handleError = (error: Error) => {
    setPending(false);

    if (error.name === 'AbortError') {
      return;
    }

    setError(error);
  };

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Make a request to the API, setting pending and error state, and return the
   * parsed response body (or true for an empty response)
   */
  const request = useCallback(
    async <T>(
      path: string,
      method: string,
      body?: unknown,
      signal?: AbortSignal
    ): Promise<T | void> => {
      setPending(true);
      setError(null);

      return fetch(`${config.api.url}${path}`, {
        method,
        headers: prepareHeaders(token),
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
      })
        .then(async response => {
          setPending(false);

          if (!response.ok) {
            setError(new Error(((await response.json()) as ApiError).message));
            return;
          }

          if (response.status === 204) {
            return true as T;
          }

          return (await response.json()) as T;
        })
        .catch(handleError);
    },
    [token]
  );

  const index = useCallback(
    async (
      options?: PaginatedRequest<IdentityGroupOrderField> & {
        name?: string;
        userId?: string;
      },
      signal?: AbortSignal
    ): Promise<PaginatedResponse<IdentityGroup> | void> => {
      const actualOptions = Object.assign(
        {},
        {
          page: 1,
          limit: 10,
          order: 'name',
          direction: 'asc',
        },
        options ?? {}
      );

      return request<PaginatedResponse<IdentityGroup>>(
        `/identity-groups?${new URLSearchParams({
          page: actualOptions.page.toString(),
          limit: actualOptions.limit.toString(),
          order: actualOptions.order,
          direction: actualOptions.direction,
          ...pluckParams(actualOptions, 'name', 'userId'),
        })}`,
        'GET',
        undefined,
        signal
      );
    },
    [request]
  );

  const show = useCallback(
    async (
      groupId: string,
      signal?: AbortSignal
    ): Promise<IdentityGroup | void> =>
      request<IdentityGroup>(
        `/identity-groups/${groupId}`,
        'GET',
        undefined,
        signal
      ),
    [request]
  );

  const create = useCallback(
    async (data: Partial<IdentityGroup>): Promise<IdentityGroup | void> =>
      request<IdentityGroup>('/identity-groups', 'POST', data),
    [request]
  );

  const update = useCallback(
    async (
      groupId: string,
      data: Partial<IdentityGroup>
    ): Promise<IdentityGroup | void> =>
      request<IdentityGroup>(`/identity-groups/${groupId}`, 'PUT', data),
    [request]
  );

  const delete_ = useCallback(
    async (groupId: string): Promise<boolean | void> =>
      request<boolean>(`/identity-groups/${groupId}`, 'DELETE'),
    [request]
  );

  const regenerateWebhookSecret = useCallback(
    async (groupId: string): Promise<IdentityGroup | void> =>
      request<IdentityGroup>(
        `/identity-groups/${groupId}/webhook-secret`,
        'POST'
      ),
    [request]
  );

  const testWebhook = useCallback(
    async (groupId: string): Promise<IdentityGroupWebhookTestResult | void> =>
      request<IdentityGroupWebhookTestResult>(
        `/identity-groups/${groupId}/webhook-test`,
        'POST'
      ),
    [request]
  );

  const upsertProvider = useCallback(
    async (
      groupId: string,
      provider: string,
      data: {
        enabled?: boolean;
        callbackMode?: IdentityCallbackMode;
        clientId?: string;
        clientSecret?: string;

        // Apple's credentials
        privateKey?: string;
        teamId?: string;
        keyId?: string;

        // Microsoft's tenant
        tenant?: string;
      }
    ): Promise<IdentityGroupProvider | void> =>
      request<IdentityGroupProvider>(
        `/identity-groups/${groupId}/providers/${provider}`,
        'PUT',
        data
      ),
    [request]
  );

  const deleteProvider = useCallback(
    async (groupId: string, provider: string): Promise<boolean | void> =>
      request<boolean>(
        `/identity-groups/${groupId}/providers/${provider}`,
        'DELETE'
      ),
    [request]
  );

  const checkProvider = useCallback(
    async (
      groupId: string,
      provider: string
    ): Promise<IdentityGroupProviderCheckResult | void> =>
      request<IdentityGroupProviderCheckResult>(
        `/identity-groups/${groupId}/providers/${provider}/check`,
        'POST'
      ),
    [request]
  );

  const startProviderTest = useCallback(
    async (
      groupId: string,
      provider: string,
      clientVerifierHash: string
    ): Promise<{ url: string; expiresAt: Date } | void> =>
      request<{ url: string; expiresAt: Date }>(
        `/identity-groups/${groupId}/providers/${provider}/test`,
        'POST',
        { clientVerifierHash }
      ),
    [request]
  );

  const completeProviderTest = useCallback(
    async (data: {
      state: string;
      code?: string;
      error?: string;
      errorDescription?: string;
      clientVerifier: string;
    }): Promise<IdentityOAuthTestResult | void> =>
      request<IdentityOAuthTestResult>(
        '/identity-groups/oauth-test/complete',
        'POST',
        data
      ),
    [request]
  );

  return {
    pending,
    error,
    resetError,
    index,
    show,
    create,
    update,
    delete: delete_,
    regenerateWebhookSecret,
    testWebhook,
    upsertProvider,
    deleteProvider,
    checkProvider,
    startProviderTest,
    completeProviderTest,
  };
};

export default useIdentityGroups;
